import { ChangeEvent, FunctionComponent, useMemo } from 'react';
import { ANALYTICS_STRINGS } from '@belong/analytics';
import useAnalyticsEvents from '@belong/providers/analyticsEvents';
import { Copy } from '@belong/storybook/components/Typography';
import { isString } from '@belong/type-utils';
import { ITestable } from '@belong/types';
import ChoiceChip, { IChoice } from '../ChoiceChip';
import { Error } from '../Error/Error';
import { Fieldset } from '../Fieldset/Fieldset';
import { ChipsetWrapper, IDirectionProp } from './ChoiceChipset.styles';

export const TEST_ID = 'ChoiceChipset';

export enum CHOICE_TYPE {
  radio = 'radio',
  checkbox = 'checkbox'
}

export interface IChoiceChipsetProps extends ITestable, IDirectionProp {
  options: (IChoice | string)[];
  legend: string;
  description?: string;
  name: string;
  selectedValue?: string | string[];
  isError?: boolean;
  errorMessage?: string;
  allowMultiSelect?: boolean;
  id?: string;
  onChange?: (value: string | string[], selection: number | number[]) => void;
}

/**
 * Collection of ChoiceChips
 */
export const ChoiceChipset: FunctionComponent<IChoiceChipsetProps> = ({
  options = [],
  legend,
  description,
  name,
  selectedValue,
  isError = false,
  errorMessage,
  allowMultiSelect = false,
  onChange,
  direction = 'row',
  id,
  'data-testid': testId = `${TEST_ID}-${name}`
}) => {
  const showError = isError && !!errorMessage;
  const testIdError = `${testId}-error`;
  const idError = `${testId}-error-id`;
  const idDescription = `${testId}-description-id`;
  const describedBy = `${showError ? idError : ''} ${description ? idDescription : ''}`.trim() || undefined;
  const inputType = allowMultiSelect ? CHOICE_TYPE.checkbox : CHOICE_TYPE.radio;
  const analytics = useAnalyticsEvents();
  const choices: IChoice[] = useMemo(
    () =>
      options.map(item => {
        if (!isString(item)) {
          return item as IChoice;
        }

        return { label: item as string, value: item as string };
      }),
    [options]
  );

  const getInitialValues = () => {
    if (selectedValue === undefined) {
      return [];
    }

    return Array.isArray(selectedValue) ? selectedValue : [selectedValue];
  };

  // [] for no selection
  const initialValues = getInitialValues();
  const selection = initialValues // returns the index of the selected value from the choices
    .map(value => choices.findIndex(item => item.value === value))
    .filter(idx => idx >= 0);

  const onSelectionChange = (event: ChangeEvent<HTMLInputElement>) => {
    // gets the selected index of the choices
    const idx = choices.findIndex(item => item.value === event.target.value);

    analytics.send({
      category: ANALYTICS_STRINGS.CATEGORY.INTERACTION,
      action: ANALYTICS_STRINGS.ACTION.CHOICE_CHIP_SELECTION,
      component: ANALYTICS_STRINGS.COMPONENT.CHOICE_CHIP,
      label: choices[idx].label
    });

    if (onChange) {
      // onChange for Radio type
      if (inputType === CHOICE_TYPE.radio) {
        onChange(choices[idx].value, idx);
        return;
      }

      // else checkbox
      const newValue = choices[idx].value;
      const newValueAlreadyInValues = initialValues.includes(newValue);

      const nextValues = newValueAlreadyInValues
        ? initialValues.filter(item => item !== newValue)
        : [...initialValues, newValue];
      // get the indexes of the nextValues from the choices and create a new array of indexes
      const nextSelections = choices
        .map((s, i) => {
          return nextValues.includes(s.value) ? i : -1;
        })
        .filter(index => index >= 0);
      onChange(nextValues, nextSelections);
    }
  };

  return (
    <Fieldset legend={legend} data-testid={testId} isFocusable={!!id} id={id} aria-invalid={isError ?? undefined}>
      {description && <Copy id={idDescription}>{description}</Copy>}
      <ChipsetWrapper direction={direction}>
        {choices.map((item, idx) => (
          <ChoiceChip
            aria-describedby={describedBy}
            key={`${item.value}${item.label}`}
            isSelected={selection.includes(idx)}
            isError={isError}
            inputType={inputType}
            onChange={onSelectionChange}
            name={name}
            {...item}
            data-testid={`${testId}-${item.value}`}
          />
        ))}
      </ChipsetWrapper>
      {showError && (
        <Error idError={idError} data-testid={testIdError}>
          {errorMessage}
        </Error>
      )}
    </Fieldset>
  );
};

ChoiceChipset.displayName = 'ChoiceChipset';
